// Dev-only entitlement-grant fixtures + their dispatcher, split out of `mocks.ts`
// on the `dashboard-mocks` precedent. It also carries the cross-tenant run lookup
// (`GET /v1/admin/fleet/runs/:runId`), which is the other admin surface that had
// a route and a screen and no mock at all — `/admin/runs/:run_id` rendered
// `<ErrorState/>`.
//
// THE GRANT GAP HID BEHIND `enabled`. `useAdminGrants` idles until a subject is
// picked, so `/admin/grants` LOADS clean and only errors once an operator uses
// it — which is why a page-load probe of the mock plane reported it healthy and
// the route was dead all the same. Fixtures keyed by subject are what make the
// picked-subject path drivable.
//
// STATEFUL, because both writes RE-READ (neither patches a row from an echo) and
// `revoked: false` — "this row was already stopped" — is a state the second
// revoke has to be able to reach.

import { at } from './mock-session';
import { unhandledMockRoute } from './unhandled-mock-route';

type Grant = {
  id: string;
  subject_kind: 'account' | 'user';
  subject_id: string;
  kind: string;
  amount: number | null;
  source: string;
  granted_by_user_id: string;
  reason: string;
  active: boolean;
  created_iso: string;
  expires_iso: string | null;
  revoked_iso: string | null;
};

/**
 * Two subjects, because switching the axis DROPS the pick and the story needs a
 * second one to switch TO. `acc_mock01` carries a live flag, a live amount grant
 * and a revoked row; `usr_mock01` carries the person-only kinds, since
 * `boxes`/`ci_slots` are account ceilings the API refuses on a user.
 */
const grants: Grant[] = [
  {
    id: 'grn_mock01',
    subject_kind: 'account',
    subject_id: 'acc_mock01',
    kind: 'unlimited_fleet',
    // A flag carries NO amount — the shape `grants_amount_ck` gates on.
    amount: null,
    source: 'operator',
    granted_by_user_id: 'usr_staff01',
    reason: 'Dogfood org — fleet metering waived',
    active: true,
    created_iso: at('2026-06-01'),
    expires_iso: null,
    revoked_iso: null,
  },
  {
    id: 'grn_mock02',
    subject_kind: 'account',
    subject_id: 'acc_mock01',
    kind: 'boxes',
    amount: 4,
    source: 'sponsor',
    granted_by_user_id: 'usr_staff01',
    reason: 'OSS sponsorship — four boxes through Q3',
    active: true,
    created_iso: at('2026-06-14'),
    expires_iso: at('2026-10-01'),
    revoked_iso: null,
  },
  {
    // Inactive with a stamp = PULLED, the half of "inactive" the table splits
    // from expiry. Without it both inactive states render identically.
    id: 'grn_mock03',
    subject_kind: 'account',
    subject_id: 'acc_mock01',
    kind: 'ci_slots',
    amount: 8,
    source: 'operator',
    granted_by_user_id: 'usr_staff01',
    reason: 'Temporary CI burst for the migration',
    active: false,
    created_iso: at('2026-05-02'),
    expires_iso: null,
    revoked_iso: at('2026-06-30'),
  },
  {
    id: 'grn_mock04',
    subject_kind: 'user',
    subject_id: 'usr_mock01',
    kind: 'concurrency',
    amount: 3,
    source: 'operator',
    granted_by_user_id: 'usr_staff01',
    reason: 'Maintainer running three repos',
    active: true,
    created_iso: at('2026-07-03'),
    expires_iso: null,
    revoked_iso: null,
  },
];

/**
 * Handle a `/v1/admin/grants*` or `/v1/admin/fleet/runs/*` request, or return
 * `null` so `mockFetch` falls through to the rest of the admin console.
 */
export function adminGrantsMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  const [bare = path, query = ''] = path.split('?');
  const reject = (): Promise<never> => unhandledMockRoute(method, path);

  if (bare.startsWith('/v1/admin/fleet/runs/')) {
    if (method !== 'GET') return reject();
    const runId = decodeURIComponent(bare.slice('/v1/admin/fleet/runs/'.length));
    // The POINT of this route: the operator is usually not a member of the org
    // that owns the run, so the server resolves the owner and names it.
    return Promise.resolve({
      run_id: runId,
      account_id: 'acc_mock01',
      account_login: 'developerz-ai',
      task_id: 'tsk_mock_live',
      run: {
        run_id: runId,
        last_seq: 42,
        last_kind: 'tool_call',
        last_at: at('2026-07-17'),
        finished: runId.endsWith('_done'),
        state: runId.endsWith('_done') ? 'finished' : 'live',
      },
    });
  }

  if (bare !== '/v1/admin/grants' && !bare.startsWith('/v1/admin/grants/')) return null;

  if (bare === '/v1/admin/grants') {
    if (method === 'GET') return Promise.resolve(subjectPage(query));
    if (method === 'POST') return Promise.resolve(issue(init));
    return reject();
  }
  if (method !== 'DELETE') return reject();
  const id = decodeURIComponent(bare.slice('/v1/admin/grants/'.length));
  const i = grants.findIndex((g) => g.id === id);
  const row = grants[i];
  if (row === undefined) return reject();
  // `revoked: false` means it was ALREADY stopped — reported, never silent.
  const revoked = row.active;
  const parked: Grant = { ...row, active: false, revoked_iso: row.revoked_iso ?? at('2026-07-20') };
  grants[i] = parked;
  return Promise.resolve({ ...parked, revoked });
}

/**
 * One subject's history, newest first. Both query halves are REQUIRED — the route
 * 400s without either, and answering about "all grants" when the operator asked
 * about one subject is exactly the confusion the picked-subject rule prevents.
 */
function subjectPage(query: string): { data: Grant[]; next_cursor: string | null } {
  const params = new URLSearchParams(query);
  const kind = params.get('subject_kind');
  const id = params.get('subject_id');
  const rows = grants.filter((g) => g.subject_kind === kind && g.subject_id === id);
  // The route mints a real offset cursor; this collection fits one page, so the
  // null is a true statement about the slice rather than a missing feature.
  return { data: rows, next_cursor: null };
}

/** Issue one. `source` and `granted_by_user_id` are SERVER-stamped, never accepted. */
function issue(init?: RequestInit): Grant {
  const body = init?.body ? (JSON.parse(String(init.body)) as Record<string, unknown>) : {};
  const row: Grant = {
    id: `grn_new_${grants.length + 1}`,
    subject_kind: (body['subject_kind'] as Grant['subject_kind']) ?? 'account',
    subject_id: String(body['subject_id'] ?? 'acc_mock01'),
    kind: String(body['kind'] ?? 'concurrency'),
    amount: typeof body['amount'] === 'number' ? body['amount'] : null,
    source: 'operator',
    granted_by_user_id: 'usr_staff01',
    reason: String(body['reason'] ?? ''),
    active: true,
    created_iso: at('2026-07-20'),
    expires_iso: typeof body['expires_at'] === 'string' ? body['expires_at'] : null,
    revoked_iso: null,
  };
  grants.push(row);
  return row;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_GRANTS = grants.map((row) => structuredClone(row));

export function resetAdminGrantsMocks(): void {
  grants.splice(0, grants.length, ...PRISTINE_GRANTS.map((row) => structuredClone(row)));
}
